/**
 * Reports: recruiterlar, trendlar, manbalar, carrier'lar, filiallar, funnel, retention, moliya (eskiz 17).
 * Davr: oy, chorak, yil yoki oraliq; tepada -- oldingi davr bilan solishtirish; har tab Excel'ga (ReportParts).
 * Doira: TL jamoasi, FM filiali, owner hammasi (yoki tanlangan filial). Filtrlar URL'da.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { Bar, BarChart, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { canPickBranch, scopedBranch, useBranchFilter } from "@/app/branchFilter";
import { useMe } from "@/features/auth/hooks";
import { can, type Me, type Permission } from "@/features/auth/types";
import { useUserOptions } from "@/features/drivers/api";
import { currentPeriod, periodLabel, previousPeriod, useBranchOptions, useReport } from "@/features/dashboards/api";
import type { BranchRow, CarrierRow, RecruiterRow, ReportSection } from "@/features/dashboards/types";
import { BranchesTable, FunnelBars, LossReasons, RetentionBars, days, pct } from "@/features/dashboards/blocks";
import { ExportButton, FinanceTab, PeriodPicker, reportDriversLink, SummaryStrip, TrendsTab } from "./ReportParts";
import type { StageEvent } from "@/features/drivers/stageMonth";
import { Badge } from "@/components/ui/badge";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { FilterSelect, Select } from "@/components/ui/field";
import { ErrorState, PageHeader, PageLoading } from "@/components/ui/misc";
import { Tabs } from "@/components/ui/tabs";
import { Table, Td, Th, Tr } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { ContextMenu } from "@/components/ui/context-menu";
import { openInNewTab } from "@/lib/clipboard";
import { branchRowMenu, carrierRowMenu, recruiterRowMenu, sourceRowMenu } from "@/features/dashboards/menu";
import { PersonLink } from "@/components/PersonLink";
import { usePersonNav } from "@/features/people/usePersonNav";

// Matnlar: i18n namespace "reports" (src/i18n/locales/reports)

const BAR = "var(--color-chart-soft)";
const BAR_DARK = "var(--color-chart)";

type Tab = ReportSection;
// ownerOnly -- filial tanlay oladiganlar (owner, ko'p filialli menejer); finance -- "budget" ruxsati bor owner/menejer
const TAB_ITEMS: { key: Exclude<Tab, "summary">; ownerOnly?: boolean; finance?: boolean }[] = [
  { key: "recruiters" },
  { key: "trends" },
  { key: "sources" },
  { key: "carriers" },
  { key: "branches", ownerOnly: true },
  { key: "funnel" },
  { key: "retention" },
  { key: "finance", finance: true },
];

export function ReportsPage() {
  const me = useMe();
  if (!me.data) return <PageLoading />;
  return <Reports me={me.data} />;
}

function Reports({ me }: { me: Me }) {
  const { t } = useTranslation("reports");
  const [params, setParams] = useSearchParams();
  const { branchId } = useBranchFilter();
  const isOwner = me.role === "owner";
  // Owner va bir nechta filialli menejer filial tanlaydi
  const pickBranch = canPickBranch(me);

  const period = params.get("period") ?? currentPeriod();
  const tab = (TAB_ITEMS.find((item) => item.key === params.get("tab"))?.key ?? "recruiters") as Tab;
  const showFinance = (me.role === "owner" || me.role === "branch_manager") && can(me, "budget");
  // Tanlay oladiganlar: URL'dagi filial, bo'lmasa yuqori paneldagi tanlov; boshqalar o'z filialiga qulflangan
  const branch = pickBranch ? (scopedBranch(me, numParam(params.get("branch")) ?? null) ?? scopedBranch(me, branchId)) : me.branch?.id;
  const recruiter = numParam(params.get("recruiter"));

  const set = (key: string, value: string | number | null | undefined) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (value === null || value === undefined || value === "") next.delete(key);
      else next.set(key, String(value));
      if (key === "branch") next.delete("recruiter");
      return next;
    });

  const query = useMemo(() => ({ period, branch: pickBranch ? branch : undefined, recruiter }), [period, branch, recruiter, pickBranch]);

  const branches = useBranchOptions(isOwner);
  const recruiters = useUserOptions({ role: "recruiter", branch: branch ?? undefined });

  const branchList = isOwner ? (branches.data?.results ?? []) : me.scope_branches;
  const scopeLabel = pickBranch
    ? (branchList.find((b) => b.id === branch)?.name ?? (isOwner ? t("scope.all") : me.scope_branches.map((b) => b.name).join(", ")))
    : (me.branch?.name ?? t("scope.branch"));

  return (
    <div>
      <PageHeader
        title={t("title")}
        subtitle={t("subtitle", { scope: scopeLabel, period: periodLabel(period), prev: periodLabel(previousPeriod(period)) })}
        actions={<ExportButton section={tab} query={query} />}
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <PeriodPicker value={period} onChange={(v) => set("period", v === currentPeriod() ? null : v)} />
        {pickBranch ? (
          <FilterSelect value={branch ?? ""} onChange={(e) => set("branch", e.target.value)} className="w-auto min-w-[150px]" aria-label={t("filters.branch")}>
            <option value="">{t(isOwner ? "filters.allBranches" : "filters.myBranches")}</option>
            {branchList.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </FilterSelect>
        ) : (
          <Select value="locked" disabled className="w-auto min-w-[150px]" aria-label={t("filters.branch")}>
            <option value="locked">{t("filters.locked", { branch: me.branch?.name ?? t("scope.branch") })}</option>
          </Select>
        )}
        <FilterSelect value={recruiter ?? ""} onChange={(e) => set("recruiter", e.target.value)} className="w-auto min-w-[160px]" aria-label={t("filters.recruiter")}>
          <option value="">{t("filters.allRecruiters")}</option>
          {recruiters.data?.results.map((u) => (
            <option key={u.id} value={u.id}>
              {u.display_name}
            </option>
          ))}
        </FilterSelect>
        {(recruiter || (pickBranch && branch) || params.get("period")) && (
          <button className="text-[12px] text-primary hover:underline" onClick={() => setParams(new URLSearchParams(tab !== "recruiters" ? { tab } : {}))}>
            {t("common:actions.clearFilters")}
          </button>
        )}
      </div>
      <SummaryStrip query={query} />
      <Tabs items={TAB_ITEMS.filter((item) => (!item.ownerOnly || pickBranch) && (!item.finance || showFinance)).map((item) => ({ key: item.key, label: t(`tabs.${item.key}`) }))} value={tab} onChange={(k) => set("tab", k === "recruiters" ? null : k)} className="mb-4" />

      {tab === "recruiters" && <RecruitersTab query={query} period={period} />}
      {tab === "sources" && <SourcesTab query={query} />}
      {tab === "carriers" && <CarriersTab query={query} />}
      {tab === "branches" && pickBranch && <BranchesTab query={query} />}
      {tab === "funnel" && <FunnelTab query={query} period={period} />}
      {tab === "retention" && <RetentionTab query={query} />}
      {tab === "trends" && <TrendsTab query={query} />}
      {tab === "finance" && showFinance && <FinanceTab query={query} narrowed={!!recruiter} />}
    </div>
  );
}

type Query = { period: string; branch?: number; recruiter?: number };

// ---------------------------------------------------------------- Recruiters

type SortKey = "hired" | "new" | "submitted" | "conversion" | "calls";

function RecruitersTab({ query, period }: { query: Query; period: string }) {
  const menus = useReportMenus();
  const { t } = useTranslation("reports");
  const report = useReport("recruiters", query);
  const funnel = useReport("funnel", query);
  const retention = useReport("retention", query);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "hired", dir: "desc" });

  const rows = useMemo(() => {
    const list = [...(report.data?.results ?? [])];
    const sign = sort.dir === "desc" ? -1 : 1;
    list.sort((a, b) => sign * ((a[sort.key] ?? -1) - (b[sort.key] ?? -1)));
    return list;
  }, [report.data, sort]);

  if (report.isPending && !report.data) return <PageLoading />;
  if (report.isError) return <ErrorState message={report.error.message} onRetry={() => report.refetch()} />;

  const toggle = (key: SortKey) => setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  const total = {
    new: sumBy(rows, "new"),
    contacted: sumBy(rows, "contacted"),
    qualified: sumBy(rows, "qualified"),
    submitted: sumBy(rows, "submitted"),
    hired: sumBy(rows, "hired"),
    calls: sumBy(rows, "calls"),
  };
  const month = periodLabel(period);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader title={t("recruiters.funnel", { month })} />
          <CardBody>
            {funnel.data ? (
              <FunnelBars
                steps={funnel.data.results.filter((s) => ["new", "contacted", "qualified", "submitted", "hired"].includes(s.status))}
                linkFor={(s) => reportDriversLink(query, s as StageEvent)}
              />
            ) : (
              <PageLoading />
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title={t("recruiters.retentionTitle")} subtitle={t("recruiters.retentionSub")} />
          <CardBody>{retention.data ? <RetentionBars rows={retention.data.results} /> : <PageLoading />}</CardBody>
        </Card>
      </div>
      <Card>
        <CardHeader title={t("recruiters.title", { count: rows.length })} subtitle={t("recruiters.sub")} />
        <CardBody className="px-0 pb-0">
          <Table variant="flush">
            <thead>
              <tr>
                <Th>{t("recruiters.cols.recruiter")}</Th>
                <SortTh label={t("recruiters.cols.new")} k="new" sort={sort} onSort={toggle} />
                <Th className="text-right">{t("recruiters.cols.contacted")}</Th>
                <Th className="text-right">{t("recruiters.cols.qualified")}</Th>
                <SortTh label={t("recruiters.cols.submitted")} k="submitted" sort={sort} onSort={toggle} />
                <SortTh label={t("recruiters.cols.hired")} k="hired" sort={sort} onSort={toggle} />
                <SortTh label={t("recruiters.cols.conv")} k="conversion" sort={sort} onSort={toggle} />
                <Th className="text-right">{t("recruiters.cols.retention")}</Th>
                <Th className="text-right">{t("recruiters.cols.avgTime")}</Th>
                <Th>{t("recruiters.cols.topLoss")}</Th>
                <SortTh label={t("recruiters.cols.calls")} k="calls" sort={sort} onSort={toggle} />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <Td colSpan={12} className="text-center text-muted">
                    {t("recruiters.empty")}
                  </Td>
                </tr>
              )}
              {rows.map((r) => (
                <ContextMenu key={r.user_id} items={() => menus.recruiter(r)}>
                  <Tr>
                    <Td className="font-semibold">
                      <PersonLink id={r.user_id} name={r.display_name} />
                    </Td>
                    <Td className="text-right">{r.new}</Td>
                    <Td className="text-right">{r.contacted}</Td>
                    <Td className="text-right">{r.qualified}</Td>
                    <Td className="text-right">{r.submitted}</Td>
                    <Td className="text-right font-semibold">
                      {r.hired}
                      {r.pending_hires > 0 && <span className="ml-1 text-[11px] font-normal text-muted">{t("recruiters.pending", { count: r.pending_hires })}</span>}
                    </Td>
                    <Td className="text-right">{pct(r.conversion)}</Td>
                    <Td className="text-right whitespace-nowrap">
                      {pct(r.retention_30)} / {pct(r.retention_60)} / {pct(r.retention_90)}
                    </Td>
                    <Td className="text-right">{days(r.avg_time_to_hire_days)}</Td>
                    <Td className="text-ink-soft">{r.top_loss_reason ?? "—"}</Td>
                    <Td className="text-right">{r.calls}</Td>
                  </Tr>
                </ContextMenu>
              ))}
              {rows.length > 1 && (
                <tr className="bg-fill/50 font-semibold">
                  <Td>{t("recruiters.total")}</Td>
                  <Td />
                  <Td className="text-right">{total.new}</Td>
                  <Td className="text-right">{total.contacted}</Td>
                  <Td className="text-right">{total.qualified}</Td>
                  <Td className="text-right">{total.submitted}</Td>
                  <Td className="text-right">{total.hired}</Td>
                  <Td className="text-right">{total.new ? `${Math.round((total.hired * 1000) / total.new) / 10}%` : "—"}</Td>
                  <Td className="text-right">—</Td>
                  <Td className="text-right">—</Td>
                  <Td />
                  <Td className="text-right">{total.calls}</Td>
                </tr>
              )}
            </tbody>
          </Table>
          <p className="px-5 py-2 text-[11px] text-muted">{t("recruiters.showing", { count: rows.length })}</p>
        </CardBody>
      </Card>
    </div>
  );
}

function SortTh({ label, k, sort, onSort }: { label: string; k: SortKey; sort: { key: SortKey; dir: "asc" | "desc" }; onSort: (k: SortKey) => void }) {
  const active = sort.key === k;
  return (
    <Th className="text-right">
      <button onClick={() => onSort(k)} className={cn("inline-flex items-center gap-1 hover:text-ink", active && "text-ink")}>
        {label}
        <span className={cn("text-[9px]", !active && "opacity-30")}>{active && sort.dir === "asc" ? "▲" : "▼"}</span>
      </button>
    </Th>
  );
}

// ---------------------------------------------------------------- Sources

function SourcesTab({ query }: { query: Query }) {
  const menus = useReportMenus();
  const { t } = useTranslation("reports");
  const sourceLabel = (s: string) => t(`common:source.${s}`, { defaultValue: s });
  const report = useReport("sources", query);
  if (report.isPending && !report.data) return <PageLoading />;
  if (report.isError) return <ErrorState message={report.error.message} onRetry={() => report.refetch()} />;
  const rows = report.data?.results ?? [];
  const chart = rows.map((r) => ({ name: sourceLabel(r.source), new: r.new, hired: r.hired }));
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
      <Card className="xl:col-span-3">
        <CardHeader title={t("sources.title")} subtitle={t("sources.sub")} />
        <CardBody className="px-0 pb-0">
          <Table variant="flush">
            <thead>
              <tr>
                <Th>{t("sources.cols.source")}</Th>
                <Th className="text-right">{t("sources.cols.new")}</Th>
                <Th className="text-right">{t("sources.cols.hired")}</Th>
                <Th className="text-right">{t("sources.cols.conversion")}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <Td colSpan={4} className="text-center text-muted">
                    {t("sources.empty")}
                  </Td>
                </tr>
              )}
              {rows.map((r) => (
                <ContextMenu key={r.source} items={() => menus.source(r.source, sourceLabel(r.source))}>
                  <Tr>
                    <Td className="font-semibold">{sourceLabel(r.source)}</Td>
                    <Td className="text-right">{r.new}</Td>
                    <Td className="text-right font-semibold">{r.hired}</Td>
                    <Td className="text-right">{pct(r.conversion)}</Td>
                  </Tr>
                </ContextMenu>
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>
      <Card className="xl:col-span-2">
        <CardHeader title={t("sources.chart")} />
        <CardBody>
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} margin={{ top: 16, right: 8, left: 8, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--color-muted)" }} axisLine={{ stroke: "var(--color-line)" }} tickLine={false} />
                <YAxis hide allowDecimals={false} />
                <Tooltip cursor={{ fill: "var(--color-sidebar)" }} contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)", color: "var(--color-ink)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span className="text-ink-soft">{v}</span>} />
                <Bar dataKey="new" name={t("sources.new")} fill={BAR} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                  <LabelList dataKey="new" position="top" style={{ fontSize: 10, fill: "var(--color-ink-soft)" }} />
                </Bar>
                <Bar dataKey="hired" name={t("sources.hired")} fill={BAR_DARK} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                  <LabelList dataKey="hired" position="top" style={{ fontSize: 10, fill: "var(--color-ink-soft)" }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- Carriers

function CarriersTab({ query }: { query: Query }) {
  const menus = useReportMenus();
  const { t } = useTranslation("reports");
  const report = useReport("carriers", query);
  if (report.isPending && !report.data) return <PageLoading />;
  if (report.isError) return <ErrorState message={report.error.message} onRetry={() => report.refetch()} />;
  const rows: CarrierRow[] = report.data?.results ?? [];
  return (
    <Card>
      <CardHeader title={t("carriers.title")} subtitle={t("carriers.sub")} />
      <CardBody className="px-0 pb-0">
        <Table variant="flush">
          <thead>
            <tr>
              <Th>{t("carriers.cols.carrier")}</Th>
              <Th className="text-right">{t("carriers.cols.submitted")}</Th>
              <Th className="text-right">{t("carriers.cols.hired")}</Th>
              <Th className="text-right">{t("carriers.cols.rejected")}</Th>
              <Th className="text-right">{t("carriers.cols.rate")}</Th>
              <Th>{t("carriers.cols.reasons")}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <Td colSpan={6} className="text-center text-muted">
                  {t("carriers.empty")}
                </Td>
              </tr>
            )}
            {rows.map((r) => (
              <ContextMenu key={r.carrier_id} items={() => menus.carrier(r)}>
                <Tr>
                  <Td className="font-semibold">{r.carrier}</Td>
                  <Td className="text-right">{r.submitted}</Td>
                  <Td className="text-right font-semibold">{r.hired}</Td>
                  <Td className="text-right">{r.rejected}</Td>
                  <Td className={cn("text-right", r.rejection_rate >= 50 && "font-semibold text-danger")}>{r.rejection_rate}%</Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {r.top_reasons.length === 0 && <span className="text-muted">—</span>}
                      {r.top_reasons.map((reason) => (
                        <Badge key={reason}>{reason}</Badge>
                      ))}
                    </div>
                  </Td>
                </Tr>
              </ContextMenu>
            ))}
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------- Branches (owner)

function BranchesTab({ query }: { query: Query }) {
  const menus = useReportMenus();
  const { t } = useTranslation("reports");
  const report = useReport("branches", { period: query.period });
  if (report.isPending && !report.data) return <PageLoading />;
  if (report.isError) return <ErrorState message={report.error.message} onRetry={() => report.refetch()} />;
  return (
    <Card>
      <CardHeader title={t("branches.title")} subtitle={t("branches.sub")} />
      <CardBody className="px-0 pb-0">
        <BranchesTable rows={report.data?.results ?? []} menu={menus.branch} />
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------- Funnel

function FunnelTab({ query, period }: { query: Query; period: string }) {
  const { t } = useTranslation("reports");
  const report = useReport("funnel", query);
  if (report.isPending && !report.data) return <PageLoading />;
  if (report.isError) return <ErrorState message={report.error.message} onRetry={() => report.refetch()} />;
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
      <Card className="xl:col-span-3">
        <CardHeader title={t("funnel.title", { period: periodLabel(period) })} subtitle={t("funnel.sub")} />
        <CardBody>
          <FunnelBars steps={report.data?.results ?? []} linkFor={(s) => reportDriversLink(query, s as StageEvent)} />
        </CardBody>
      </Card>
      <Card className="xl:col-span-2">
        <CardHeader title={t("funnel.lossTitle")} subtitle={t("funnel.lossSub")} />
        <CardBody>
          <LossReasons rows={report.data?.loss_reasons ?? []} />
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- Retention

function RetentionTab({ query }: { query: Query }) {
  const { t } = useTranslation("reports");
  const report = useReport("retention", query);
  if (report.isPending && !report.data) return <PageLoading />;
  if (report.isError) return <ErrorState message={report.error.message} onRetry={() => report.refetch()} />;
  const rows = report.data?.results ?? [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        {rows.map((r) => (
          <Card key={r.days} className="px-5 py-4">
            <div className="text-[12px] text-ink-soft">{t("retention.card", { days: r.days })}</div>
            <div className="mt-1 text-[34px] leading-tight font-bold">{pct(r.rate)}</div>
            <div className="mt-1 text-[11px] text-muted">{r.eligible ? t("retention.stillWorking", { retained: r.retained, eligible: r.eligible }) : t("retention.none", { days: r.days })}</div>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader title={t("retention.title")} subtitle={t("retention.sub")} />
        <CardBody>
          <RetentionBars rows={rows} />
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- yordamchilar

/** Hisobot qatorlari uchun o'ng tugma menyulari; recruiter -- shu hisobot ichida filtrlanadi */
function useReportMenus() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { t } = useTranslation("menu");
  const { setBranchId } = useBranchFilter();
  const personNav = usePersonNav();
  const me = useMe().data;
  const paths = { navigate: (path: string) => navigate(path), openNewTab: openInNewTab, profile: personNav, can: (perm: Permission) => can(me, perm) };
  const focus = (key: string, value: number) => {
    const next = new URLSearchParams(params);
    next.set(key, String(value));
    setParams(next, { replace: true });
  };
  return {
    recruiter: (r: { user_id: number; display_name: string }) => recruiterRowMenu(r, { ...paths, focus: () => focus("recruiter", r.user_id) }, t),
    source: (source: string, label: string) => sourceRowMenu(source, label, paths, t),
    carrier: (r: { carrier_id: number; carrier: string }) => carrierRowMenu(r, paths, t),
    branch: (r: BranchRow) =>
      branchRowMenu(
        r,
        {
          ...paths,
          navigate: (path: string) => (path.startsWith("/reports?branch=") ? focus("branch", r.branch_id) : navigate(path)),
          openDashboard: () => {
            setBranchId(r.branch_id);
            navigate("/");
          },
          openBudget: () => {
            setBranchId(r.branch_id);
            navigate("/budget");
          },
        },
        t,
      ),
  };
}

function numParam(value: string | null): number | undefined {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function sumBy(rows: RecruiterRow[], key: "new" | "contacted" | "qualified" | "submitted" | "hired" | "calls"): number {
  return rows.reduce((acc, r) => acc + r[key], 0);
}
